import { ACCESS_MAP, CATEGORY_ORDER, ID_PATTERNS, SIGNATURES } from './signatures';
import {
  AccessChecklistItem,
  AuditInsight,
  CMS_ASSET_PATH_SEGMENTS,
  DetectionHit,
  Detections,
  EvidenceBlob,
  ExtractedIds,
  FRAMEWORKS_HEADLESS_CAPABLE,
  GOOGLE_TAG_HOSTS,
  HeadlessAssessment,
  HeadlessConfidence,
  InsightSeverity,
  ScanResult,
  TRADITIONAL_CMS
} from './martech-scanner-constants';

/** Ported from martech_scanner.py's SiteScanner.analyze() signature-matching loop. */
export const detectPlatforms = (evidenceBlobs: EvidenceBlob[]): Detections => {
  const detections: Detections = {};

  Object.entries(SIGNATURES).forEach(([category, sigs]) => {
    Object.entries(sigs).forEach(([name, patterns]) => {
      const hits: DetectionHit[] = [];
      for (const pattern of patterns) {
        // Single match() per blob instead of a test() scan followed by a second match()
        // scan of the same text — halves the regex work for these unbounded patterns.
        for (const blob of evidenceBlobs) {
          const match = blob.text.match(pattern);
          if (match) {
            hits.push({
              pattern: pattern.source,
              source: blob.source,
              match: (match[0] ?? '').slice(0, 120)
            });
            break;
          }
        }
      }
      if (hits.length > 0) {
        detections[category] ??= {};
        detections[category][name] = hits;
      }
    });
  });

  return detections;
};

/** Ported from martech_scanner.py's ID extraction step in SiteScanner.analyze(). */
export const extractIds = (allText: string): ExtractedIds => {
  const ids: ExtractedIds = {};

  Object.entries(ID_PATTERNS).forEach(([label, pattern]) => {
    const globalPattern = new RegExp(
      pattern.source,
      pattern.flags.includes('g') ? pattern.flags : `${pattern.flags}g`
    );
    const found = new Set<string>();
    let match = globalPattern.exec(allText);
    while (match !== null) {
      found.add(match[1] ?? match[0]);
      match = globalPattern.exec(allText);
    }
    if (found.size > 0) {
      ids[label] = Array.from(found).sort();
    }
  });

  return ids;
};

/**
 * Unions a quick scan's Detections with a full scan's (see
 * full-scan-merge-utils.ts) rather than one replacing the other. A full
 * scan's page discovery is sitemap.xml only, which can surface a smaller or
 * different page set than a quick scan's HTML link-extraction, so its
 * evidence isn't guaranteed to be a superset - a platform/pattern the quick
 * scan already confirmed must survive even if the full scan's own evidence
 * doesn't happen to reproduce it. Hits are deduped by source+match, and each
 * survivor is tagged with which scan(s) actually produced it (`seenIn`) so
 * the UI can flag a finding the full scan never re-confirmed, or one it
 * found that the quick scan missed.
 */
export const mergeDetections = (
  quickDetections: Detections,
  fullDetections: Detections
): Detections => {
  const merged: Detections = {};
  const addHit = (
    category: string,
    platform: string,
    hit: DetectionHit,
    source: 'quick' | 'full'
  ) => {
    merged[category] = merged[category] ?? {};
    merged[category][platform] = merged[category][platform] ?? [];
    const hits = merged[category][platform];
    const existing = hits.find(h => h.source === hit.source && h.match === hit.match);
    if (existing) {
      existing.seenIn = existing.seenIn?.includes(source)
        ? existing.seenIn
        : [...(existing.seenIn ?? []), source];
    } else {
      hits.push({ ...hit, seenIn: [source] });
    }
  };
  Object.entries(quickDetections).forEach(([category, platforms]) =>
    Object.entries(platforms).forEach(([platform, hits]) =>
      hits.forEach(hit => addHit(category, platform, hit, 'quick'))
    )
  );
  Object.entries(fullDetections).forEach(([category, platforms]) =>
    Object.entries(platforms).forEach(([platform, hits]) =>
      hits.forEach(hit => addHit(category, platform, hit, 'full'))
    )
  );
  return merged;
};

/** Unions two ExtractedIds objects the same way mergeDetections does. */
export const mergeExtractedIds = (a: ExtractedIds, b: ExtractedIds): ExtractedIds => {
  const merged: ExtractedIds = {};
  [a, b].forEach(ids => {
    Object.entries(ids).forEach(([label, values]) => {
      merged[label] = Array.from(new Set([...(merged[label] ?? []), ...values])).sort();
    });
  });
  return merged;
};

const isFirstParty = (url: string, domain: string): boolean => {
  try {
    const host = new URL(url).hostname.replace(/^www\./, '');
    return host === '' || host === domain || host.endsWith(`.${domain}`);
  } catch {
    return false;
  }
};

const GTM_LOADER_PATH = /\/gtm\.js$|\/gtag\/js$/;
const CONTAINER_ID_QUERY = /[?&]id=(?:GTM|G|AW|DC)-/i;

/**
 * Whether a GTM/gtag loader URL has the shape of a *container endpoint* — the
 * thing a tagging server actually exposes — rather than a static copy of the
 * loader that a CMS happens to serve first-party.
 *
 * A real sGTM endpoint lives at (or just under) the host root and carries the
 * container id, e.g. `https://sgtm.example.com/gtm.js?id=GTM-ABC123`. A CMS
 * copy lives several directories deep inside a module/plugin/theme path, e.g.
 * Drupal's `https://www.example.com/modules/contrib/google_tag/js/gtm.js?tlruwe`,
 * which used to be reported as first-party sGTM serving.
 */
const looksLikeTaggingEndpoint = (parsed: URL): boolean => {
  const path = parsed.pathname.toLowerCase();
  if (!GTM_LOADER_PATH.test(path)) return false;
  if (CMS_ASSET_PATH_SEGMENTS.some(segment => path.includes(segment))) return false;
  if (CONTAINER_ID_QUERY.test(parsed.search)) return true;
  // No container id in the query, so fall back to depth: `/gtm.js` or
  // `/<custom-prefix>/gtm.js` (sGTM supports a path prefix), and likewise
  // `/gtag/js` or `/<custom-prefix>/gtag/js`.
  const depth = path.split('/').filter(Boolean).length;
  return depth <= (path.endsWith('/gtag/js') ? 3 : 2);
};

/**
 * Heuristics for first-party / server-side tagging. Ported from
 * SiteScanner.detect_server_side — merge the returned category into the
 * "Server-Side Tagging Signals" bucket of the base detections.
 */
export const detectServerSideTagging = (
  scriptSrcs: string[],
  allText: string,
  domain: string
): Record<string, DetectionHit[]> => {
  const results: Record<string, DetectionHit[]> = {};
  const add = (label: string, hit: DetectionHit) => {
    results[label] ??= [];
    results[label].push(hit);
  };

  scriptSrcs.forEach(src => {
    let parsed: URL;
    try {
      parsed = new URL(src);
    } catch {
      return;
    }
    const host = parsed.hostname.toLowerCase();
    if (looksLikeTaggingEndpoint(parsed) && host && !GOOGLE_TAG_HOSTS.some(g => host.includes(g))) {
      add('GTM loaded via custom domain (likely sGTM first-party serving)', {
        pattern: 'gtm.js on non-Google host',
        source: 'script-srcs',
        match: src.slice(0, 160)
      });
    }
  });

  const collectMatch = allText.match(/['"](https?:\/\/[^'"]+?\/g\/collect[^'"]*)['"]/);
  if (collectMatch && isFirstParty(collectMatch[1], domain)) {
    add('First-party GA4 collection endpoint', {
      pattern: '/g/collect on first-party',
      source: 'inline-js',
      match: collectMatch[1].slice(0, 160)
    });
  }

  const transportMatch = allText.match(/transport_url['"]?\s*[:,]\s*['"](https?:\/\/[^'"]+)/);
  if (transportMatch) {
    add('gtag transport_url configured (sGTM)', {
      pattern: 'transport_url',
      source: 'inline-js',
      match: transportMatch[1].slice(0, 160)
    });
  }

  const sstSubdomainPattern = new RegExp(
    `https?://(?:sgtm|sst|ss|gtm|data|metrics|analytics|track|collect|tags?)\\.${domain.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}`,
    'i'
  );
  const subdomainMatch = allText.match(sstSubdomainPattern);
  if (subdomainMatch) {
    add('Tagging-style first-party subdomain in use', {
      pattern: 'sgtm/sst/metrics/track subdomain',
      source: 'page',
      match: subdomainMatch[0].slice(0, 160)
    });
  }

  return results;
};

/** Ported from martech_scanner.py's SiteScanner.assess_headless. */
export const assessHeadlessArchitecture = (detections: Detections): HeadlessAssessment => {
  const frameworks = new Set(Object.keys(detections['Frontend Framework'] ?? {}));
  const headlessCms = new Set(Object.keys(detections['Headless CMS / Content API'] ?? {}));
  const cms = new Set(Object.keys(detections['Platform / CMS'] ?? {}));
  const traditional = [...cms].filter(c => TRADITIONAL_CMS.has(c));
  const modernFw = [...frameworks].filter(f => FRAMEWORKS_HEADLESS_CAPABLE.has(f));

  const signals: string[] = [];

  if (headlessCms.size > 0 && modernFw.length > 0) {
    signals.push(
      `Modern JS framework (${modernFw.join(', ')}) + headless CMS/content API (${[...headlessCms].join(', ')})`
    );
    return { verdict: 'Headless', confidence: HeadlessConfidence.HIGH, signals };
  }
  if (cms.has('Shopify Hydrogen (headless)')) {
    signals.push('Shopify Hydrogen / Oxygen detected');
    return { verdict: 'Headless (Shopify Hydrogen)', confidence: HeadlessConfidence.HIGH, signals };
  }
  if (modernFw.length > 0 && traditional.length > 0) {
    signals.push(
      `Framework ${modernFw.join(', ')} present alongside ${traditional.join(', ')} markers — could be headless ${traditional[0]} or embedded components`
    );
    return {
      verdict: 'Possibly hybrid / headless frontend over a CMS backend',
      confidence: HeadlessConfidence.MEDIUM,
      signals
    };
  }
  if (modernFw.length > 0 && traditional.length === 0) {
    signals.push(
      `SSG/SSR framework detected (${modernFw.join(', ')}) with no traditional CMS markers`
    );
    return {
      verdict: 'Likely headless or fully custom JS app',
      confidence: HeadlessConfidence.MEDIUM,
      signals
    };
  }
  if (headlessCms.size > 0) {
    signals.push(`Headless CMS references found: ${[...headlessCms].join(', ')}`);
    return {
      verdict: 'Possibly headless (content API in use)',
      confidence: HeadlessConfidence.MEDIUM,
      signals
    };
  }
  if (traditional.length > 0) {
    signals.push(`Traditional platform markers: ${traditional.join(', ')}`);
    return {
      verdict: 'Not headless (traditional architecture)',
      confidence: HeadlessConfidence.HIGH,
      signals
    };
  }
  signals.push('No strong platform or framework markers found');
  return { verdict: 'Undetermined', confidence: HeadlessConfidence.LOW, signals };
};

/** Collapse detections into a deduplicated onboarding access checklist. Ported from build_access_checklist. */
export const buildAccessChecklist = (detections: Detections): AccessChecklistItem[] => {
  const seen = new Set<string>();
  const checklist: AccessChecklistItem[] = [];

  CATEGORY_ORDER.forEach(category => {
    const names = Object.keys(detections[category] ?? {}).sort();
    names.forEach(name => {
      const info = ACCESS_MAP[name];
      if (info && !seen.has(info.access)) {
        seen.add(info.access);
        checklist.push({ platform: name, request: info.access, how: info.howTo });
      }
    });
  });

  return checklist;
};

/** Rule-based advertiser-relevant observations. Ported from generate_insights. */
export const generateInsights = (
  detections: Detections,
  ids: ExtractedIds,
  headless: HeadlessAssessment
): AuditInsight[] => {
  const insights: AuditInsight[] = [];
  const add = (severity: AuditInsight['severity'], title: string, note: string) =>
    insights.push({ severity, title, note });

  const flat = new Set(Object.values(detections).flatMap(cat => Object.keys(cat)));
  const tagMgrs = new Set(Object.keys(detections['Tag Management'] ?? {}));
  tagMgrs.delete('Google Tag (gtag.js)');
  const ads = new Set([
    ...Object.keys(detections['Advertising & Pixels'] ?? {}),
    ...Object.keys(detections['DSP / Programmatic'] ?? {})
  ]);
  const consent = new Set(Object.keys(detections['Consent Management'] ?? {}));
  const sst = detections['Server-Side Tagging Signals'] ?? {};
  const sstNames = Object.keys(sst);

  if (flat.has('Universal Analytics (legacy)')) {
    add(
      InsightSeverity.RISK,
      'Legacy Universal Analytics tags still on site',
      'UA stopped processing data in 2023/24. Leftover UA tags suggest the migration was never fully ' +
        'cleaned up — a quick credibility win in the pitch, and worth auditing the GA4 setup quality.'
    );
  }
  if (!flat.has('Google Analytics 4') && (ads.size > 0 || tagMgrs.size > 0)) {
    add(
      InsightSeverity.RISK,
      'No GA4 detected',
      'Ad platforms are present but no GA4 measurement was found in source. Either analytics is ' +
        "missing (major gap) or loaded via rules we can't see statically — verify with a browser scan."
    );
  }
  if ((ids['GA4 Measurement IDs']?.length ?? 0) > 1) {
    add(
      InsightSeverity.NOTE,
      `Multiple GA4 measurement IDs found (${ids['GA4 Measurement IDs'].slice(0, 4).join(', ')})`,
      'Could be intentional (rollup + regional) or duplicate/legacy tagging inflating data. Audit before ' +
        'using GA4 as a source of truth.'
    );
  }
  if ((ids['GTM Container IDs']?.length ?? 0) > 1) {
    add(
      InsightSeverity.NOTE,
      `Multiple GTM containers (${ids['GTM Container IDs'].slice(0, 4).join(', ')})`,
      'Multiple containers on one site often means fragmented ownership (site vs. previous agency). ' +
        'Clarify who publishes to each.'
    );
  }

  if (tagMgrs.size === 0 && ads.size >= 2) {
    add(
      InsightSeverity.OPPORTUNITY,
      'Multiple ad pixels hardcoded with no tag manager',
      'Pixels appear to be hardcoded in the page. Migrating to GTM gives faster launches, versioning, ' +
        'and consent integration — a strong early deliverable.'
    );
  }
  if (tagMgrs.size >= 2) {
    add(
      InsightSeverity.RISK,
      `Multiple tag management systems active (${[...tagMgrs].sort().join(', ')})`,
      'Running two TMS layers usually indicates a stalled migration or split team ownership. Expect ' +
        'duplicate firing risk; clarify which is authoritative before adding tags.'
    );
  }

  if (sstNames.length > 0) {
    add(
      InsightSeverity.NOTE,
      'Server-side tagging infrastructure detected',
      'This is a relatively sophisticated setup. Confirm who owns and hosts the server container ' +
        '(client, dev team, or a previous agency) — access to it is required for conversion tracking changes.'
    );
  } else if (ads.has('Meta (Facebook) Pixel') || ads.has('Google Ads Conversion')) {
    add(
      InsightSeverity.OPPORTUNITY,
      'No server-side tagging detected',
      'Client relies on browser-side pixels only. Server-side GTM + Meta Conversions API / enhanced ' +
        'conversions is a concrete signal-durability pitch (ad blockers, ITP, consent-driven signal loss).'
    );
  }

  const realCmps = new Set(consent);
  ['IAB TCF present', 'US Privacy / GPP present', 'Google Consent Mode'].forEach(k =>
    realCmps.delete(k)
  );
  if (realCmps.size === 0 && (ads.size > 0 || flat.has('Google Analytics 4'))) {
    add(
      InsightSeverity.RISK,
      'No consent management platform detected',
      'Tracking is running with no visible CMP. Depending on the client’s markets (EU/UK, CA/CPRA, ' +
        'WA My Health My Data, etc.) this is a compliance exposure — and an easy value-add conversation.'
    );
  }
  if (
    realCmps.size > 0 &&
    !consent.has('Google Consent Mode') &&
    (ads.has('Google Ads Conversion') || flat.has('Google Analytics 4'))
  ) {
    add(
      InsightSeverity.RISK,
      'CMP present but no Google Consent Mode signals found',
      'No gcd parameter was observed on Google hits during the scan (and no consent-mode calls in ' +
        'source). Without Consent Mode v2, Google Ads audience/measurement features degrade and modeled ' +
        'conversions are unavailable — this is required for EEA audience features. Verify at checkout too.'
    );
  }

  if (/^(Headless|Likely headless|Possibly)/.test(headless.verdict)) {
    add(
      InsightSeverity.NOTE,
      'Headless / JS-framework architecture',
      'Tagging changes may require dev involvement rather than CMS plugins. Prioritize a dataLayer ' +
        "spec and confirm the client's deploy cadence — it affects launch timelines."
    );
  }
  if (flat.has('Shopify')) {
    add(
      InsightSeverity.NOTE,
      'Shopify platform',
      'Checkout tagging must go through Customer Events / native channel integrations (checkout.liquid ' +
        'is deprecated). Confirm plan tier (Plus unlocks more) and which app-based pixels are installed.'
    );
  }
  if (
    flat.has('Sitecore') ||
    flat.has('Adobe Experience Manager') ||
    flat.has('Salesforce Commerce Cloud (SFCC)')
  ) {
    add(
      InsightSeverity.NOTE,
      'Enterprise CMS with dev-gated releases',
      'Expect tagging changes to route through client engineering sprints. Build lead time into launch plans.'
    );
  }

  if (flat.has('Google Optimize (sunset)')) {
    add(
      InsightSeverity.RISK,
      'Google Optimize references found (product sunset in 2023)',
      'Dead testing tool still referenced — cleanup item, and an opening to discuss a replacement.'
    );
  }
  if (
    ads.has('Meta (Facebook) Pixel') &&
    !sst['Meta Conversions API Gateway'] &&
    sstNames.length === 0
  ) {
    add(
      InsightSeverity.OPPORTUNITY,
      'Meta Pixel without visible Conversions API',
      'Browser-only Meta signal. CAPI (direct, via sGTM, or platform-native e.g. Shopify) typically ' +
        'improves event match quality and attributed conversions.'
    );
  }
  const replay = new Set(Object.keys(detections['Session Replay / UX Analytics'] ?? {}));
  const testing = new Set(Object.keys(detections['A/B Testing & Personalization'] ?? {}));
  if (replay.size > 0 && testing.size === 0) {
    add(
      InsightSeverity.OPPORTUNITY,
      'Session replay present but no testing/personalization tool',
      "They're watching user behavior but not acting on it — natural CRO conversation."
    );
  }

  return insights;
};

export interface DetectionPipelineResult {
  detections: Detections;
  extractedIds: ExtractedIds;
  headlessAssessment: HeadlessAssessment;
  accessChecklist: AccessChecklistItem[];
  advertiserInsights: AuditInsight[];
}

/**
 * The full detection sequence (signature matching -> server-side-tagging
 * merge -> ID extraction -> headless assessment -> access checklist ->
 * insights), extracted so both the quick static scan and a full-scan merge
 * can run it identically over their respective evidence pools.
 */
export const runDetectionPipeline = (
  evidenceBlobs: EvidenceBlob[],
  scriptSrcs: string[],
  domain: string
): DetectionPipelineResult => {
  const detections = detectPlatforms(evidenceBlobs);
  const allText = evidenceBlobs.map(b => b.text).join('\n');
  // Merge into whatever detectPlatforms already filed under this category
  // (Stape, Snowplow, Meta CAPI Gateway, ...) - DEV-24211: this used to be a
  // plain assignment, which silently discarded those signature hits whenever
  // this heuristic pass found anything.
  const sst = detectServerSideTagging(scriptSrcs, allText, domain);
  if (Object.keys(sst).length > 0) {
    detections['Server-Side Tagging Signals'] = {
      ...detections['Server-Side Tagging Signals'],
      ...sst
    };
  }
  const extractedIds = extractIds(allText);
  const headlessAssessment = assessHeadlessArchitecture(detections);
  return {
    detections,
    extractedIds,
    headlessAssessment,
    accessChecklist: buildAccessChecklist(detections),
    advertiserInsights: generateInsights(detections, extractedIds, headlessAssessment)
  };
};

/**
 * Client/pitch-ready markdown brief. Ported from render_brief.
 * DEV-24211: `isFullScan` lets the footer describe what evidence actually
 * produced `result` - the caller passes report.scanSource, since ScanResult
 * itself carries no provenance and a full-scan merge otherwise leaves the
 * exported brief claiming to be a static scan after a real browser crawl.
 */
export const renderBrief = (result: ScanResult, isFullScan = false): string => {
  const lines: string[] = [];
  const w = (line: string) => lines.push(line);
  const { detections: det, headlessAssessment: ha, extractedIds: ids } = result;

  w(`# Website & Martech Assessment — ${result.domain}`);
  w(`*Scanned ${result.scannedAt.slice(0, 10)} · ${result.pagesScanned.length} page(s) analyzed*`);
  w('');

  const platform =
    Object.keys(det['Platform / CMS'] ?? {})
      .sort()
      .join(', ') || 'Not identified';
  const tms =
    Object.keys(det['Tag Management'] ?? {})
      .sort()
      .join(', ') || 'None detected';
  const nAds =
    Object.keys(det['Advertising & Pixels'] ?? {}).length +
    Object.keys(det['DSP / Programmatic'] ?? {}).length;
  const sst = det['Server-Side Tagging Signals'] ?? {};
  const sstNames = Object.keys(sst).sort();
  const cmp = Object.keys(det['Consent Management'] ?? {}).filter(
    c => !['IAB TCF present', 'US Privacy / GPP present', 'Google Consent Mode'].includes(c)
  );

  w('## Executive Summary');
  w(`- **Site platform:** ${platform}`);
  w(`- **Architecture:** ${ha.verdict} (confidence: ${ha.confidence})`);
  w(`- **Tag management:** ${tms}`);
  w(`- **Active ad platforms/pixels detected:** ${nAds}`);
  w(
    `- **Server-side tagging:** ${sstNames.length > 0 ? `Signals detected — ${sstNames.join(', ')}` : 'None detected'}`
  );
  w(`- **Consent management:** ${cmp.length > 0 ? cmp.sort().join(', ') : 'None detected'}`);
  w('');

  w('## Detected Technology Stack');
  w('');
  w('| Category | Platforms |');
  w('|---|---|');
  CATEGORY_ORDER.forEach(cat => {
    const names = Object.keys(det[cat] ?? {});
    if (names.length > 0) {
      w(`| ${cat} | ${names.sort().join(', ')} |`);
    }
  });
  w('');

  if (Object.keys(ids).length > 0) {
    w('## Key IDs');
    w('');
    Object.entries(ids).forEach(([label, vals]) => {
      w(`- **${label}:** ${vals.slice(0, 10).join(', ')}`);
    });
    w('');
  }

  if (result.advertiserInsights.length > 0) {
    w('## Observations & Opportunities');
    w('');
    const order = { RISK: 0, OPPORTUNITY: 1, NOTE: 2 };
    const icon = { RISK: '⚠️', OPPORTUNITY: '💡', NOTE: 'ℹ️' };
    [...result.advertiserInsights]
      .sort((a, b) => order[a.severity] - order[b.severity])
      .forEach(insight => {
        w(`**${icon[insight.severity]} ${insight.title}**`);
        w('');
        w(insight.note);
        w('');
      });
  }

  if (result.accessChecklist.length > 0) {
    w('## Access Required for Onboarding');
    w('');
    w('| ✓ | Access to request | Detected via | How it’s granted |');
    w('|---|---|---|---|');
    result.accessChecklist.forEach(item => {
      w(`| ☐ | ${item.request} | ${item.platform} | ${item.how} |`);
    });
    w('');
  }

  w('---');
  w(
    isFullScan
      ? '*Full-browser scan: pages were rendered and crawled with a real browser, so tags fired via ' +
          'tag-manager rules at runtime are captured, not just what appears in delivered HTML/JS.*'
      : '*Static scan of delivered HTML/JS. Tags fired only via tag-manager rules at ' +
          'runtime may not appear; a browser-based verification is recommended before finalizing scope.*'
  );

  return lines.join('\n');
};
